import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex-composition-helpers'
import moment from 'moment/moment'
import JobAttachment from '@/models/job_attachment'
import { Attachment } from '@/models/attachment'
import User from '@/models/user'
import { AcceptedImageExtensions } from '@/utils/constants'
import { getInstance } from '@/auth'
import { User as UserAuth0 } from '@auth0/auth0-spa-js/dist/typings/global'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsDialog from '@/components/vision/VsDialog.vue'
import VsButton from '@/components/vision/VsButton.vue'
import VsHeading from '@/components/vision/VsHeading.vue'
import AttachmentCard from '@/components/tickets/AttachmentCard.vue'
import ImageAttachment from '@/components/common/ImageAttachment.vue'
import VsText from '@/components/vision/VsText.vue'

type AttachmentType = JobAttachment | Attachment


export default /*#__PURE__*/_defineComponent({
  __name: 'Attachments',
  props: {
    jobUuid: { default: null },
    clientOnly: { type: Boolean },
    jobAttachments: null,
    attachmentStorageId: { default: '' },
    enableDelete: { type: Boolean }
  },
  emits: ['attachment-removed'],
  setup(__props: any, { emit }) {

const props = __props as { jobUuid: string | null, clientOnly: boolean, jobAttachments: JobAttachment[], attachmentStorageId: string | null, enableDelete: boolean }





onMounted(() => {
  if (props.attachmentStorageId) fetchAttachments(props.attachmentStorageId)
})

const auth: { user: UserAuth0 } = getInstance()

const isDeleteDialogVisible = ref(false)

const currentlySelectedAttachment = ref<AttachmentType | null>(null)

const loadingAttachments = ref<string[]>([])

const store = useStore()

const getAttachments = computed<(storageId: string) => Attachment[]>(
  () => store.getters['attachments/getAllAttachmentsForStorageId']
)
const getUserBySid = computed<(userSid: string) => User>(
  () => store.getters['users/getBySid']
)

const canDeleteAttachments = computed<boolean>(
  () => store.getters['permissions/canDeleteAttachments']
)

const isLoading = computed(() => loadingAttachments.value.length > 0)

const fetchAttachments = async (attachmentStorageId: string | null) =>
  await store.dispatch('attachments/fetchAttachments', attachmentStorageId)

const deleteAttachment = async (arg: { filename: string; storageId: string }) =>
  await store.dispatch('attachments/deleteAttachment', arg)

const removeJobAttachment = async (arg: {
  jobId: string
  attachmentId: string
}) => await store.dispatch('jobs/removeAttachment', arg)

const firebaseAttachments = computed(() =>
  props.attachmentStorageId
    ? getAttachments.value(props.attachmentStorageId)
    : []
)
const attachments = computed(() => [
  ...firebaseAttachments.value,
  ...[...props.jobAttachments].sort((a, b) => a.created.diff(b.created)),
])

function isJobAttachment(attachment: AttachmentType | null) {
  return attachment instanceof JobAttachment
}

function getUploadInfo(attachment: AttachmentType) {
  const user = getUserBySid.value(
    (isJobAttachment(attachment)
      ? (attachment as JobAttachment).createdBy
      : (attachment as Attachment).metadata?.createdBy) ?? ''
  )
  const userName = user ? user.name : null

  const uploadedTime = moment(
    isJobAttachment(attachment)
      ? (attachment as JobAttachment).created.local()
      : (attachment as Attachment).metadata?.timestamp
  ).format('MMM DD, HH:mm')

  return userName ? `${userName} on ${uploadedTime}` : `${uploadedTime}`
}

function canDelete(attachment: AttachmentType) {
  const isUploader =
    (isJobAttachment(attachment)
      ? (attachment as JobAttachment).createdBy
      : (attachment as Attachment).metadata?.createdBy) === auth.user.sub
  return canDeleteAttachments.value || isUploader
}

function deleteAttachmentMethod() {
  if (isJobAttachment(currentlySelectedAttachment.value)) {
    emit(
      'attachment-removed',
      (currentlySelectedAttachment.value as JobAttachment).id
    )

    if (!props.clientOnly) {
      removeJobAttachment({
        jobId: props.jobUuid ?? '',
        attachmentId: (
          currentlySelectedAttachment.value as JobAttachment
        ).id.toString(),
      })
    }
  } else {
    deleteAttachment({
      filename: (currentlySelectedAttachment.value as Attachment).filename,
      storageId: props.attachmentStorageId ?? '',
    })
  }
  closeDialog()
}

function isImage(filename: string) {
  const extension = filename.split('.').pop()

  return AcceptedImageExtensions.includes(extension?.toLowerCase() ?? '')
}

function openDialog(attachment: AttachmentType | null) {
  currentlySelectedAttachment.value = attachment
  isDeleteDialogVisible.value = true
}

function closeDialog() {
  currentlySelectedAttachment.value = null
  isDeleteDialogVisible.value = false
}

function getDownloadUrl(attachment: AttachmentType) {
  return isJobAttachment(attachment)
    ? (attachment as JobAttachment).source
    : (attachment as Attachment).downloadUrl
}

function hasMetadata(attachment: AttachmentType) {
  return isJobAttachment(attachment) || !!(attachment as Attachment).metadata
}

/**
 * Check if we need to refresh Azure Storage attachment links before letting route
 */
async function handleAttachmentClick(attachment: AttachmentType, event: Event) {
  // We only check jobAttachments for link expiration
  if (!isJobAttachment(attachment) || isTokenValid(attachment as JobAttachment))
    return

  // Prevent link from opening since token is expired
  event.preventDefault()

  await refreshAttachment(attachment as JobAttachment)
  window.open((attachment as JobAttachment).source)
}

function isTokenValid(attachment: JobAttachment) {
  const url = attachment.source
  const urlParams = new URLSearchParams(url)
  const expiry = urlParams.get('se')

  if (expiry) {
    const date = Date.parse(expiry)
    const now = Date.parse(new Date().toISOString())
    return date > now
  }

  // Could not get expiry from the url. Assume still valid to prevent any infinite loops
  return true
}

async function refreshAttachment(attachment: JobAttachment) {
  markAttachmentLoading(attachment.id)

  const refreshedAttachment = await store.dispatch('jobs/fetchAttachment', {
    jobId: props.jobUuid,
    attachmentId: attachment.id,
  })

  if (refreshedAttachment) {
    // Job side-sheet is not reactive to store updates so we force it here by updating the source property
    attachment.source = refreshedAttachment.source
  }

  markAttachmentLoaded(attachment.id)
}

function markAttachmentLoading(attachmentId: string) {
  if (loadingAttachments.value.indexOf(attachmentId) !== -1) return

  loadingAttachments.value.push(attachmentId)
}

function markAttachmentLoaded(attachmentId: string) {
  const index = loadingAttachments.value.indexOf(attachmentId)
  if (index === -1) return

  loadingAttachments.value.splice(index, 1)
}

function isAttachmentLoading(attachment: AttachmentType): boolean {
  if (!isJobAttachment(attachment)) return false

  return loadingAttachments.value.includes((attachment as JobAttachment).id)
}

return { __sfc: true,props, emit, auth, isDeleteDialogVisible, currentlySelectedAttachment, loadingAttachments, store, getAttachments, getUserBySid, canDeleteAttachments, isLoading, fetchAttachments, deleteAttachment, removeJobAttachment, firebaseAttachments, attachments, isJobAttachment, getUploadInfo, canDelete, deleteAttachmentMethod, isImage, openDialog, closeDialog, getDownloadUrl, hasMetadata, handleAttachmentClick, isTokenValid, refreshAttachment, markAttachmentLoading, markAttachmentLoaded, isAttachmentLoading, VsWrapper, VsDialog, VsButton, VsHeading, AttachmentCard, ImageAttachment, VsText }
}

})